import React from 'react';
import PropTypes from 'prop-types';

import Product from '../../../../Model/Product';
import { formatCurrencyCents } from '../../../../utils/formatters';
import { formatPluralDuration } from '../../../../utils/durationUnit';
import FeaturesList from '../FeaturesList';
import texts from './texts';
import {
  CATEGORY_CONFIG,
  formatAcceptUntil,
  getAcceptUntilState,
  getChallengeStart,
} from './utils';
import {
  AcceptUntilDate,
  AcceptUntilLabel,
  AcceptUntilRow,
  CardHeaderRow,
  ChallengeStartDate,
  DisabledText,
  FeatureDivider,
  PackageCard,
  PackageName,
  PriceRow,
  PriceTermText,
  PriceText,
  StyledRadio,
} from './styles';

/**
 * One offering in an Inside Sales category, as a card the rep picks from.
 *
 * An offering is either a package or a challenge. A package renews, so it reads as its initial
 * payment then its monthly rate; a challenge is paid once for a fixed run, so it reads as a
 * duration, shows when it starts (or that it is ongoing), and closes with the date enrollment
 * stops. Both take their includes and excludes from the product they belong to.
 *
 * A challenge past its accept-until date still shows, greyed out and marked disabled, so the rep
 * can see it exists but cannot pick it.
 */
const PackageCategoryCard = ({
  pkg,
  contract,
  product,
  isSelected,
  isReadOnly,
  isDisabled,
  onClick,
}) => {
  const offering = contract || pkg;
  const catConfig = CATEGORY_CONFIG[offering.category] || {};
  const { Tag: CategoryTag } = catConfig;
  const acceptUntil = contract ? formatAcceptUntil(contract.acceptUntil) : '';
  const challengeStart = contract ? getChallengeStart(contract.startDate) : null;
  const acceptUntilState = contract ? getAcceptUntilState(contract.acceptUntil) : null;

  const priceDisplay = formatCurrencyCents(
    offering.initialPaymentInCents,
    offering.currency,
    { minimumFractionDigits: 0 },
  );

  const termDisplay = contract
    ? texts.forDuration(formatPluralDuration(contract.initialTerm, contract.durationUnit))
    : texts.forTermThenMonthly(
      pkg.initialTerm,
      formatCurrencyCents(pkg.monthlyPaymentInCents, pkg.currency, { minimumFractionDigits: 0 }),
    );

  return (
    <PackageCard
      $selected={isSelected}
      $disabled={isReadOnly || isDisabled}
      $isOfferingDisabled={isDisabled}
      $acceptUntilState={acceptUntilState}
      onClick={isDisabled ? undefined : onClick}
      aria-disabled={isDisabled}
    >
      <CardHeaderRow>
        {CategoryTag && <CategoryTag>{catConfig.label}</CategoryTag>}
        <PriceRow>
          <PackageName style={{ marginRight: 'auto' }}>{offering.name}</PackageName>
          {isDisabled && <DisabledText>{texts.disabled}</DisabledText>}
          <StyledRadio
            checked={isSelected}
            disabled={isDisabled}
            size="small"
            disableRipple
            onClick={(e) => e.stopPropagation()}
          />
        </PriceRow>
      </CardHeaderRow>
      <PriceRow style={{ marginBottom: '6px' }}>
        <PriceText>{priceDisplay}</PriceText>
        <PriceTermText>{termDisplay}</PriceTermText>
      </PriceRow>
      {!!product && (
        <>
          <FeatureDivider />
          <FeaturesList productDoc={product} />
        </>
      )}
      {(!!challengeStart || !!acceptUntil) && <FeatureDivider />}
      {!!challengeStart && (
        <AcceptUntilRow>
          <AcceptUntilLabel>{texts.starts}</AcceptUntilLabel>
          <ChallengeStartDate>{challengeStart.isOngoing ? texts.ongoing : challengeStart.date}</ChallengeStartDate>
        </AcceptUntilRow>
      )}
      {!!acceptUntil && (
        <AcceptUntilRow>
          <AcceptUntilLabel>{texts.acceptUntil}</AcceptUntilLabel>
          <AcceptUntilDate>{acceptUntil}</AcceptUntilDate>
        </AcceptUntilRow>
      )}
    </PackageCard>
  );
};

PackageCategoryCard.propTypes = {
  // A card shows one of the two: a package, or the BASE contract that defines a challenge.
  pkg: PropTypes.object,
  contract: PropTypes.object,
  product: PropTypes.instanceOf(Product),
  isSelected: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

PackageCategoryCard.defaultProps = {
  pkg: null,
  contract: null,
  product: null,
  isReadOnly: false,
  isDisabled: false,
};

export default PackageCategoryCard;
